// @oasis-permission-inherits: /videos (mirage-videos:view)
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Clock, ListFilter, Play, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";
import {
  blockedMessage,
  checkpointOptions,
  filterPanelsByViewerStatus,
  nextReviewablePanelId,
  orderPanelsForReview,
} from "~/modules/dpo-review/panels";
import { listDpoReviewPanelsQuery } from "~/modules/dpo-review/queries";
import type { DpoReviewPanelSummary, DpoReviewViewerStatus } from "~/modules/dpo-review/schemas";

export const Route = createFileRoute("/_dash/videos/evals/dpo-review/")({
  component: RouteComponent,
});

const ALL_CHECKPOINTS = "__all__";
const STATUS_OPTIONS: ReadonlyArray<{
  value: DpoReviewViewerStatus;
  label: string;
}> = [
  { value: "not_started", label: "Needs review" },
  { value: "in_progress", label: "In progress" },
  { value: "submitted", label: "Submitted" },
];
const DEFAULT_STATUSES: DpoReviewViewerStatus[] = ["not_started", "in_progress"];

function RouteComponent() {
  const navigate = useNavigate();
  const [checkpoint, setCheckpoint] = useState<string>(ALL_CHECKPOINTS);
  const [statuses, setStatuses] = useState<DpoReviewViewerStatus[]>(DEFAULT_STATUSES);
  const panelsQuery = listDpoReviewPanelsQuery.useQuery({ params: {} }, { retry: false });

  const allPanels = panelsQuery.data?.panels ?? [];
  const checkpoints = checkpointOptions(allPanels);
  const checkpointPanels =
    checkpoint === ALL_CHECKPOINTS
      ? allPanels
      : allPanels.filter((panel) => panel.checkpointSlug === checkpoint);
  const panels = orderPanelsForReview(filterPanelsByViewerStatus(checkpointPanels, statuses));

  const reviewable = panels.filter((panel) => panel.reviewable);
  const waiting = panels.filter((panel) => !panel.reviewable);
  const queueHead = nextReviewablePanelId(panels, null);
  const queueLength = panels.filter(
    (panel) => panel.reviewable && panel.viewerStatus !== "submitted"
  ).length;
  const statusFilterLabel = statusSelectionLabel(statuses);

  const setStatusSelected = (status: DpoReviewViewerStatus, selected: boolean) => {
    setStatuses((current) => {
      if (selected) {
        return current.includes(status) ? current : [...current, status];
      }
      return current.filter((candidate) => candidate !== status);
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-2">
            <Button asChild variant="link" size="sm" className="h-auto p-0">
              <Link to="/videos/evals">Back to evals</Link>
            </Button>
          </div>
          <h1 className="text-xl font-bold">DPO Review</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Each panel is one flagged segment and every reroll mined for it. Mark one best segment
            and the defective ones, or ask for more segments when the mined round cannot produce a
            pair. Reviews are recorded per reviewer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            disabled={!queueHead}
            title={queueHead ? undefined : "Nothing left to review on this checkpoint"}
            onClick={() =>
              queueHead &&
              void navigate({
                to: "/videos/evals/dpo-review/$panelId",
                params: { panelId: queueHead },
                search: { queue: true },
              })
            }
          >
            <Play className="size-4" />
            Review all ({queueLength})
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => panelsQuery.refetch()}
            disabled={panelsQuery.isFetching}
          >
            <RefreshCw className={cn("size-4", panelsQuery.isFetching && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={checkpoint} onValueChange={setCheckpoint}>
          <SelectTrigger className="h-8 w-[420px] max-w-full font-mono text-xs">
            <SelectValue placeholder="All checkpoints" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CHECKPOINTS}>All checkpoints</SelectItem>
            {checkpoints.map((option) => (
              <SelectItem
                key={option.checkpointSlug}
                value={option.checkpointSlug}
                className="font-mono text-xs"
              >
                {option.checkpointSlug} — {option.reviewableCount}/{option.panelCount} reviewable
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="max-w-full">
              <ListFilter className="size-4" />
              Your status: {statusFilterLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52">
            {STATUS_OPTIONS.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={statuses.includes(option.value)}
                onCheckedChange={(checked) => setStatusSelected(option.value, checked === true)}
                onSelect={(event) => event.preventDefault()}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {panelsQuery.isLoading && (
        <div className="rounded-md border p-5 text-sm text-muted-foreground">Loading panels...</div>
      )}

      {panelsQuery.error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load panels</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{panelsQuery.error.message}</pre>
        </div>
      )}

      {!panelsQuery.isLoading && !panelsQuery.error && checkpointPanels.length === 0 && (
        <div className="rounded-md border p-5 text-sm text-muted-foreground">
          Nothing mined yet. Panels appear once the mining service completes a segment&apos;s first
          round of nine.
        </div>
      )}

      {!panelsQuery.isLoading &&
        !panelsQuery.error &&
        checkpointPanels.length > 0 &&
        panels.length === 0 && (
          <div className="rounded-md border p-5 text-sm text-muted-foreground">
            No panels match the selected statuses.
          </div>
        )}

      {reviewable.length > 0 && (
        <PanelTable
          title="Ready to review"
          icon={<CheckCircle2 className="size-4" />}
          panels={reviewable}
        />
      )}

      {waiting.length > 0 && (
        <PanelTable
          title="Waiting on the miner"
          icon={<Clock className="size-4" />}
          panels={waiting}
        />
      )}
    </div>
  );
}

function PanelTable({
  icon,
  panels,
  title,
}: {
  icon: React.ReactNode;
  panels: DpoReviewPanelSummary[];
  title: string;
}) {
  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        {icon}
        {title}
      </h2>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Segment</TableHead>
              <TableHead>Checkpoint</TableHead>
              <TableHead>Mined</TableHead>
              <TableHead>Your state</TableHead>
              <TableHead>Submissions</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {panels.map((panel) => {
              const blocked = blockedMessage(panel);
              return (
                <TableRow key={panel.panelId}>
                  <TableCell>
                    <div className="font-mono text-xs">{panel.segmentHash.slice(0, 12)}</div>
                    <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                      {panel.videoId
                        ? `video ${panel.videoId} · segment ${panel.segmentId}`
                        : "video unknown — flagged before identity capture"}
                    </div>
                    {panel.flagReason && (
                      <div className="mt-1 max-w-md truncate text-xs text-muted-foreground">
                        {panel.flagReason}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{panel.checkpointSlug}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {panel.visibleClipCount} shown
                    {panel.minedSeedCount !== panel.visibleClipCount && (
                      <span className="text-muted-foreground"> / {panel.minedSeedCount} mined</span>
                    )}
                    <div className="text-[11px] text-muted-foreground">
                      round {panel.visibleRounds} · cap {panel.seedCap}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "font-mono text-xs",
                        panel.viewerStatus === "submitted" && "text-emerald-500",
                        panel.viewerStatus === "in_progress" && "text-amber-600"
                      )}
                    >
                      {statusLabel(panel.viewerStatus)}
                    </span>
                    {blocked && (
                      <div className="mt-1 max-w-xs text-[11px] text-muted-foreground">
                        {blocked}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{panel.submittedCount}</TableCell>
                  <TableCell className="text-right">
                    {panel.reviewable ? (
                      <Button asChild size="xs">
                        <Link
                          to="/videos/evals/dpo-review/$panelId"
                          params={{ panelId: panel.panelId }}
                        >
                          Review
                        </Link>
                      </Button>
                    ) : (
                      <Button size="xs" variant="outline" disabled title={blocked ?? undefined}>
                        Waiting
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function statusLabel(status: DpoReviewViewerStatus): string {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

function statusSelectionLabel(statuses: readonly DpoReviewViewerStatus[]): string {
  if (statuses.length === STATUS_OPTIONS.length) {
    return "All statuses";
  }
  if (statuses.length === 0) {
    return "No statuses";
  }
  return STATUS_OPTIONS.filter((option) => statuses.includes(option.value))
    .map((option) => option.label)
    .join(", ");
}
